/** @module csi/view/customview/SelectPlugin */
define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/aspect",
	"dojo/dom-class",
	"dojo/store/Memory",
	"lodash",
	"src/c8-platform/service/customViewBuilderService",
	"csi",
	"csi/locale",
	"csi/middleware/customView/customViewLeftPanel/customViewDialogLeftPanelService",
	"csi/prefs",
	"csi/prefs/Summary",
	"csi/sessionConstants",
	"csi/store/GroupQueryEngine",
	"csi/view/ToolbarPluginSelect",
	"./_SelectPluginComboBoxMenu",
], function(declare, lang, aspect, domClass, Memory, _, {getValidatedCustomViewWithSourceView, queryCustomViews},
	csi, locale, {saveFavorite, removeFavorite}, csiPrefs, Summary,
	sessionConstants, GroupQueryEngine, ToolbarPluginSelect, _SelectPluginComboBoxMenu) {

	const GROUP_FAVORITE = "1";
	const GROUP_SYSTEM_WIDE = "2";
	const GROUP_SHARED = "3";
	const GROUP_PERSONAL = "4";

	return declare(ToolbarPluginSelect, {
		pageSize: Infinity,
		value: csi.EMPTY_URL,

		/** @attribute {boolean} storeCreated - indicate that there are no need to recreate store */
		storeCreated: false,

		/**
		 * @override
		 * Initialize widget
		 */
		postMixInProperties: function() {
			const view = this.getView();

			// add preview capability in drop-down menu
			this.dropDownClass = declare(_SelectPluginComboBoxMenu, {
				popupWrappperCss: "csi-flex-row csi-custom-view-popup",
				previewWidgetClass: Summary,
				previewWidgetParams: {view},

				onInitialSelection: function() {
					return view.getSelectedCustomView();
				}
			});

			this.fetchProperties = {
				group: {
					id: function(item) {
						return item.favorite ? GROUP_FAVORITE :
							item.systemWide ? GROUP_SYSTEM_WIDE :
								item.personal ? GROUP_PERSONAL : GROUP_SHARED;
					},
					nameFunc: function(id) {
						let resId;
						switch (id) {
							case GROUP_FAVORITE: resId = "Favorites";
								break;
							case GROUP_SYSTEM_WIDE: resId = "ViewPreferences_Site";
								break;
							case GROUP_PERSONAL: resId = "PersonalView";
								break;
							case GROUP_SHARED: resId = "SharedView";
								break;
						}
						return locale.resource(resId);
					}
				}
			};

			// create single item store to show combo box placeholder (only until user opens dropdown)
			const onCustomViewApplied = () => {
				const {customViewApplied} = view;
				const {name, urlNode} = customViewApplied;
				const store = new Memory({data: [this.getCustomViewData(customViewApplied)]});
				this.storeCreated = false;
				this.set("store", store);
				this.set("value", urlNode, false, name, customViewApplied);
			};

			this.own(
				view.watch("customViewDirty", lang.hitch(this, this._onWatchDirty)),
				view.watch("customViewApplied", onCustomViewApplied),
			);

			this.inherited(arguments);
		},

		/**
		 * process results of getCustomViews request and create memory store
		 * @param {ICustomViewInfoResponse} results
		 */
		createStore: function(results) {
			const data = [];
			const self = this;
			const view = this.getView();
			const {customViewContainerUrl} = view;
			const {getCustomViewData} = this;
			const {favorites, names, personal, roles, systemWide} = results;
			const viewRoles = {};
			roles.forEach(role => {
				const {url, views} = role;
				views.forEach(view => {
					if (!viewRoles[view]) {
						viewRoles[view] = [url];
					} else {
						viewRoles[view].push(url);
					}
				});
			});
			let viewPersonal = [];
			const personalUrls = {};
			personal.forEach(person => {
				viewPersonal = viewPersonal.concat(person.views);
				const {views, url} = person;
				views.forEach(view => {
					personalUrls[view] = url;
				})

			});
			// don't show OOTB default if there are user defined system wide default
			const defaults = csiPrefs.getDefaults();
			const idContext = view.getViewPreferenceContext();
			if (!defaults.hasSystemWideDefault(idContext)) {
				data.push(getCustomViewData(view.getDefaultCustomView()));
			}

			// sort names by favorites
			const nonFavoriteKeys = Object.keys(names).filter(key => !favorites.includes(key));
			const sortedKeys = [...favorites, ...nonFavoriteKeys];
			const sortedNames = {};
			sortedKeys.forEach(key => {
				sortedNames[key] = names[key];
			});

			for (key in sortedNames) {
				data.push({
					id: key,
					name: names[key],
					urlNode: key,
					favorite: favorites.includes(key),
					personal: viewPersonal.includes(key),
					personalUrls,
					systemWide: systemWide.includes(key),
					viewRoles: viewRoles[key] || [],
					getName: function() {
						return this.name;
					},
					getIcon: function() {
						return this.personal ? "person" : this.systemWide ? "public" : "people";
					},
					setFavorite: function(domNode) {
						// in this function this === item
						this.favorite = !this.favorite;
						domNode.innerText = this.favorite ? "star" : "star_outline";
						domClass.toggle(domNode, "csi-select-favorite", this.favorite);
						self._startSearch(self._lastInput || "");

						if (this.favorite) {
							saveFavorite(customViewContainerUrl, this.id);
						} else {
							removeFavorite(customViewContainerUrl, this.id);
						}
					},
					async getItemDetails(cnl) {
						if (!this.itemDetails) {
							const [node, sourceNode] = await getValidatedCustomViewWithSourceView(cnl);
							this.itemDetails = view.newCustomViewFromNode(node, sourceNode);
						}

						return this.itemDetails;
					},
					isDefaultView: () => false,
					isCurrentUser: function() {
						return personalUrls[this.urlNode] === sessionConstants.userUrl;
					},
					isShared: function() {
						return !!this.viewRoles.length;
					},
					isSystemWide: function() {
						return this.systemWide;
					},
				})
			}
			const options = {
				cssProperty: "css",
				data,
				idProperty: "id",
				queryEngine: GroupQueryEngine,
			}
			this.storeCreated = true;
			return new Memory(options);
		},

		/**
		 * Create special store item without detailed CV info
		 * used for default and customViewApplied CVs
		 * member function can only be called for default CV
		 * @param {PreferenceView} cv
		 */
		getCustomViewData(cv) {
			return {
				id: cv.urlNode,
				itemDetails: cv,
				name: cv.name,
				urlNode: cv.urlNode,
				favorite: false,
				personal: false,
				personalUrls: {},
				systemWide: true,
				viewRoles: [],
				getName: () => cv.name,
				getIcon: () => "public",
				setFavorite: () => { },
				getItemDetails: () => cv,
				isDefaultView: () => true,
				isCurrentUser: () => false,
				isShared: () => false,
				isSystemWide: () => true,
			}
		},

		/**
		 * @override
		 * only rebuild store if it is not yet created or if customViewApplied was updated
		 */
		loadAndOpenDropDown() {
			this.set("query", csiPrefs.getStoreSelectQuery(this.getView().get("customViewContext")));

			if (this.storeCreated) {
				this.inherited(arguments);
				return;
			}

			const view = this.getView();
			const {customViewContainerUrl, customViewId} = view;
			const ctx = view.getQueryRelativeContext?.();

			queryCustomViews(customViewContainerUrl, ctx?.getNodeType())
				.then(((results) => {
					const store = this.createStore(results);
					this.set("store", store);

					if (customViewId) {
						this.set("value", customViewId, false, store.get(customViewId));
					}
					this.storeCreated = true;
					this.inherited(arguments);
				}))
				.catch((error) => {
					view.onViewError(error);
				});
		},

		/**
		 * sort dropdown search results
		 * @override
		 */
		onSearch: function(res, query, options) {
			this.inherited(arguments);
			res.sort((a, b) => {
				// only sort same group options (do not sort favorites)
				if (a._isGroup || b._isGroup || a.favorite || b.favorite || (a.systemWide != b.systemWide) || (a.personal != b.personal)) {
					return 0;
				}
				return a.name.localeCompare(b.name);
			})
		},

		/**
		 * Update CSS to show dirty symbol
		 * @param {string} attr
		 * @param {string} oldVal
		 * @param {string} newVal
		 */
		_onWatchDirty: function(attr, oldVal, newVal) {
			domClass.toggle(this.domNode, "csiCustomViewSelectDirty", newVal);
		},

		/**
		 * value setter
		 * @param {string} value 
		 * @param {boolean?} priorityChange 
		 * @param {string?} displayedValue 
		 * @param {item?} item 
		 */
		_setValueAttr: function(value, priorityChange, displayedValue, item) {
			if (this.store) {
				this.inherited(arguments);
			}
		},

	});
});